import {
  latestTieInsReferencesKey,
  tieInsInfiniteKey,
  useLatestTieInsReferencesInfinite,
  useTieIn,
  useTieInsInfinite,
} from "@common/api/task-transcripts"
import { InlineEmpty } from "@common/components/shared/inline-empty"
import { Button } from "@common/components/ui/button"
import { Skeleton } from "@common/components/ui/skeleton"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Link2 } from "lucide-react"
import { Suspense, useEffect } from "react"
import { z } from "zod"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { LoadMoreSentinel } from "@/components/layout/load-more-sentinel"
import { NameFilter } from "@/components/layout/name-filter"
import { TieInGroupedTable } from "./-components/tie-in-grouped-table"
import { TieInTable } from "./-components/tie-in-table"

type View = "all" | "grouped"

const VIEWS: View[] = ["all", "grouped"]

const filterSchema = z.object({
  view: z.enum(["all", "grouped"]).optional().catch("grouped"),
  search: z.string().optional().catch(""),
  id: z.string().uuid().optional().catch(undefined),
})

export const Route = createFileRoute("/_layout/tie-in")({
  staticData: { breadcrumb: "Tie-In" },
  validateSearch: (search) => filterSchema.parse(search),
  component: TieInPage,
})

function TieInPage(): React.ReactElement {
  const { view = "grouped", search = "", id } = Route.useSearch()

  if (id !== undefined) {
    return (
      <ListPage toolbar={<SingleToolbar />} detail={null}>
        <SingleTieIn id={id} />
      </ListPage>
    )
  }

  return (
    <ListPage toolbar={<Toolbar view={view} search={search} />} detail={null}>
      <Suspense fallback={<ListSkeleton />}>
        <ViewResults view={view} search={search} />
      </Suspense>
    </ListPage>
  )
}

function SingleToolbar(): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <ListToolbar
      actions={
        <>
          <span className="mr-auto text-body-medium text-muted-foreground">
            Showing one tie-in
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate({ search: (prev) => ({ ...prev, id: undefined }) })
            }
          >
            Show all tie-ins
          </Button>
        </>
      }
    />
  )
}

function SingleTieIn({ id }: { id: string }): React.ReactElement {
  useRefreshTieInsOnLeave()
  const { data: tieIn, isLoading, isError } = useTieIn(id)

  if (isLoading) {
    return <ListSkeleton />
  }
  if (isError || !tieIn) {
    return <InlineEmpty>Tie-in not found.</InlineEmpty>
  }
  return <TieInTable tieIns={[tieIn]} />
}

function ViewResults({
  view,
  search,
}: {
  view: View
  search: string
}): React.ReactElement {
  switch (view) {
    case "all":
      return <FlatResults search={search} />
    case "grouped":
      return <GroupedResults search={search} />
  }
}

function Toolbar({
  view,
  search,
}: {
  view: View
  search: string
}): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={VIEWS}
          tab={view}
          label={(option) => (option === "all" ? "All" : "Grouped")}
          onSelect={(next) =>
            navigate({ search: (prev) => ({ ...prev, view: next }) })
          }
        />
      }
      actions={
        <NameFilter
          search={search}
          onSearch={(next) =>
            navigate({
              search: (prev) => ({ ...prev, search: next || undefined }),
            })
          }
          placeholder="Search tie-ins…"
        />
      }
    />
  )
}

function useRefreshTieInsOnLeave(): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: latestTieInsReferencesKey })
      queryClient.invalidateQueries({ queryKey: tieInsInfiniteKey })
    }
  }, [queryClient])
}

function FlatResults({ search }: { search: string }): React.ReactElement {
  useRefreshTieInsOnLeave()
  const {
    tieIns,
    totalCount,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTieInsInfinite(search)

  if (totalCount === 0) {
    return <TieInsEmpty search={search} />
  }

  return (
    <>
      <TieInTable tieIns={tieIns} />
      <LoadMoreSentinel
        noun="tie-ins"
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isError={isFetchNextPageError}
        fetchNextPage={fetchNextPage}
      />
    </>
  )
}

function GroupedResults({ search }: { search: string }): React.ReactElement {
  useRefreshTieInsOnLeave()
  const {
    references,
    totalCount,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLatestTieInsReferencesInfinite(search, null, true)
  const unassigned = useTieInsInfinite(search, true)

  if (totalCount === 0 && unassigned.totalCount === 0) {
    return <TieInsEmpty search={search} />
  }

  return (
    <>
      <TieInGroupedTable
        references={references}
        unassigned={{
          tieIns: unassigned.tieIns,
          hasNextPage: unassigned.hasNextPage,
          isFetchingNextPage: unassigned.isFetchingNextPage,
          isFetchNextPageError: unassigned.isFetchNextPageError,
          fetchNextPage: unassigned.fetchNextPage,
        }}
      />
      <LoadMoreSentinel
        noun="tie-ins"
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isError={isFetchNextPageError}
        fetchNextPage={fetchNextPage}
      />
    </>
  )
}

function TieInsEmpty({ search }: { search: string }): React.ReactElement {
  return (
    <ListEmpty
      icon={Link2}
      title={search ? "No matching tie-ins" : "No tie-ins"}
      body={
        search
          ? "Try a different search term."
          : "Tie-ins will appear here once they are recorded."
      }
    />
  )
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="space-y-2 px-2 py-3">
      {[1, 2, 3, 4, 5].map((index) => (
        <Skeleton key={index} className="h-12 w-full rounded-md" />
      ))}
    </div>
  )
}
